import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { listCartItems } from '../api/Cart';

interface CartContextType {
    totalItems: number;
    fetchCartCount: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchCartCount = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setTotalItems(0);
                return;
            }
            const response = await listCartItems();
            if (response && response.items) {
                 // Calculate total number of items (sum of quantities) or just number of unique items
                 // Usually for badge it's number of unique items line count, or total quantity.
                 // The user said "display total item in cart".
                 // response.total should be the total count of records (unique products).
                 setTotalItems(response.total);
            }
        } catch (error) {
            console.error("Failed to fetch cart count", error);
        }
    }, []);

    useEffect(() => {
        fetchCartCount();
        
        // Listen for custom event to update cart (optional but useful)
        const handleCartUpdate = () => fetchCartCount();
        window.addEventListener('cart-updated', handleCartUpdate);
        
        return () => {
            window.removeEventListener('cart-updated', handleCartUpdate);
        };
    }, [fetchCartCount]);

    return (
        <CartContext.Provider value={{ totalItems, fetchCartCount }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
